import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search as SearchIcon, Loader2, ArrowRight, CornerDownLeft,
  LayoutDashboard, GraduationCap, BookOpen, ClipboardList, CheckSquare, Trophy,
  Award, Rocket, Calculator, Sparkles, Database, Users, UserPlus, Tag, FolderOpen,
  Folder, Calendar, MessageCircle, Bell, User, UserCircle, Settings, CreditCard,
  Sparkle, HelpCircle,
} from "lucide-react";
import { api } from "../lib/api";

// Map the backend `icon` slug to a lucide component so each result row
// renders with a semantically meaningful glyph. Fallback → Search.
const ICON = {
  "layout-dashboard": LayoutDashboard,
  "graduation-cap":   GraduationCap,
  "book-open":        BookOpen,
  "clipboard-list":   ClipboardList,
  "check-square":     CheckSquare,
  "trophy":           Trophy,
  "award":            Award,
  "rocket":           Rocket,
  "calculator":       Calculator,
  "sparkles":         Sparkles,
  "database":         Database,
  "users":            Users,
  "user-plus":        UserPlus,
  "tag":              Tag,
  "folder-open":      FolderOpen,
  "folder":           Folder,
  "calendar":         Calendar,
  "message-circle":   MessageCircle,
  "bell":             Bell,
  "user":             User,
  "user-circle":      UserCircle,
  "settings":         Settings,
  "credit-card":      CreditCard,
  "sparkle":          Sparkle,
  "help-circle":      HelpCircle,
};

/**
 * Modern SaaS-style global search palette that lives in the top bar.
 *
 * UX notes:
 *   • Debounced fetch (150 ms) hits `/api/search` and receives categorised
 *     ranked groups. Empty query renders no dropdown.
 *   • Flat item list is derived from groups on every render so ↑ / ↓ can
 *     traverse across sections without extra bookkeeping.
 *   • Cmd/Ctrl+K globally focuses the input. Escape blurs.
 *   • Clicking outside closes the palette; clicking a row navigates and
 *     closes.
 */
export default function GlobalSearch({ autoFocus = false, onSubmit } = {}) {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const rootRef  = useRef(null);
  const listRef  = useRef(null);
  const reqIdRef = useRef(0);

  const [query,     setQuery]     = useState("");
  const [groups,    setGroups]    = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [open,      setOpen]      = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  // Derive the flat list of navigable items so keyboard nav works across
  // categorised sections. Order matches the visual order.
  const flatItems = useMemo(() => {
    const out = [];
    for (const g of groups) {
      for (const it of g.items || []) out.push({ ...it, _category: g.category });
    }
    return out;
  }, [groups]);

  // Reset the active row whenever the result set changes so the caret
  // never points at a stale index (which would render as no highlight).
  useEffect(() => { setActiveIdx(0); }, [flatItems.length, query]);

  // ── Debounced fetch ────────────────────────────────────────────────
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setGroups([]); setTotal(0); setLoading(false);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}&per_category=5`);
        // Drop stale responses if the user kept typing.
        if (myReq !== reqIdRef.current) return;
        setGroups(r.data?.groups || []);
        setTotal(r.data?.total || 0);
      } catch (_) {
        if (myReq !== reqIdRef.current) return;
        setGroups([]); setTotal(0);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [query]);

  // ── Keyboard shortcut: Cmd/Ctrl+K opens & focuses ──────────────────
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey);
      if (isCmdK) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus on mount when the caller opts in (used by the mobile
  // search overlay so the keyboard springs open the moment the user
  // taps the magnifier).
  useEffect(() => {
    if (autoFocus) {
      // rAF so the input is definitely in the DOM before we focus it.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Click-outside to close ─────────────────────────────────────────
  useEffect(() => {
    const onDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const navigateTo = useCallback((href) => {
    if (!href) return;
    setOpen(false);
    // Trim any hash so `/modules/x#quiz` still routes via react-router; we
    // then let the target page scroll to the anchor if it exists.
    const [path, hash] = href.split("#");
    nav(path);
    if (hash) {
      // wait one tick for the page to mount before scrolling
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 220);
    }
    // Notify the caller (mobile overlay uses this to auto-close after
    // navigation so the search row doesn't linger on top of the new page).
    onSubmit?.();
  }, [nav, onSubmit]);

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, flatItems.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = flatItems[activeIdx];
      if (it) navigateTo(it.href);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Keep the highlighted row in view as the user arrows through results.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const q = query.trim();
  const showDropdown = open && q.length > 0;
  const showEmpty    = showDropdown && !loading && total === 0;

  // Flat index per group so we can address rows for keyboard highlight.
  let cursor = 0;

  return (
    <div ref={rootRef} className="relative" data-testid="global-search-root">
      <div className="relative">
        <SearchIcon className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground md:text-muted-foreground text-white/80 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="topbar-search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (q) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="Search the Institute"
          className="w-full rounded-full border border-white/20 md:border-shell-border bg-white/10 md:bg-muted/60 text-white md:text-foreground placeholder:text-white/70 md:placeholder:text-muted-foreground pl-9 pr-14 py-2 text-sm focus:outline-none focus:border-shell-accent"
          aria-label="Global search"
          aria-expanded={showDropdown}
          role="combobox"
          autoComplete="off"
        />
        <kbd
          className="hidden lg:inline-flex items-center gap-1 absolute right-3 top-1.5 text-[10px] font-semibold text-muted-foreground bg-white border border-shell-border rounded px-1.5 py-0.5"
          aria-hidden="true"
        >
          {typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? "⌘" : "Ctrl"} K
        </kbd>
      </div>

      {showDropdown && (
        <div
          data-testid="global-search-dropdown"
          className="absolute left-0 right-0 mt-2 rounded-xl border border-shell-border bg-white shadow-2xl overflow-hidden z-50"
          style={{ maxHeight: "70vh" }}
        >
          <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: "calc(70vh - 40px)" }}>
            {loading && total === 0 && (
              <div className="px-4 py-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="global-search-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Searching…
              </div>
            )}

            {showEmpty && (
              <div className="px-4 py-8 text-center" data-testid="global-search-empty">
                <div className="text-sm font-medium text-shell-side">No results found</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Try a shorter or different keyword.
                </div>
              </div>
            )}

            {!showEmpty && groups.map((g) => (
              <div key={g.category} className="py-1" data-testid={`global-search-group-${g.category.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  {g.category}
                </div>
                {g.items.map((it) => {
                  const idx = cursor++;
                  const Icon = ICON[it.icon] || SearchIcon;
                  const active = idx === activeIdx;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-idx={idx}
                      data-testid={`global-search-item-${it.id}`}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => navigateTo(it.href)}
                      className={
                        "w-full flex items-center gap-3 px-4 py-2 text-left transition-colors " +
                        (active ? "bg-shell-accent/10" : "hover:bg-muted/60")
                      }
                    >
                      <span className={"w-8 h-8 rounded-lg flex items-center justify-center shrink-0 " + (active ? "bg-shell-accent/20 text-shell-accent" : "bg-muted text-shell-side")}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-shell-side truncate">{it.title}</span>
                        {it.subtitle && (
                          <span className="block text-xs text-muted-foreground truncate">{it.subtitle}</span>
                        )}
                      </span>
                      {active && <ArrowRight className="w-3.5 h-3.5 text-shell-accent shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="border-t border-shell-border bg-muted/30 px-3 py-1.5 flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-white border border-shell-border rounded text-[10px]">↑</kbd><kbd className="px-1 py-0.5 bg-white border border-shell-border rounded text-[10px]">↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> Open</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-white border border-shell-border rounded text-[10px]">Esc</kbd> Close</span>
            {total > 0 && <span className="ml-auto">{total} result{total === 1 ? "" : "s"}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
